import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { formatDateToAPI } from '../../helpers/UtilsFunctions';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { TextField, MenuItem } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';


const EmployeeForm = ({ isOpen, onClose, onSubmit, editData = null }) => {
  const { mode } = useThemeMode();
  const isEditMode = !!editData;

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role_id: '',
    full_name: '',
    birthdate: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [showFormLoading, setShowFormLoading] = useState(false);

  // Debug: Log roles state changes
  useEffect(() => {
    console.log('EmployeeForm roles state:', roles);
    console.log('EmployeeForm roles length:', roles.length);
  }, [roles]);

  // Static role data for now
  const staticRoles = [
    { id: 1, name: 'Admin' },
    { id: 2, name: 'Manager' },
    { id: 3, name: 'Employee' },
    { id: 4, name: 'HR Admin' }
  ];

  // Initialize form data when editData changes
  useEffect(() => {
    if (editData) {
      console.log('Loading edit data:', editData);
      
      // Ensure role_id is converted to string for the TextField component
      const roleId = editData.role_id !== null && editData.role_id !== undefined 
        ? String(editData.role_id) 
        : '';
      
      setFormData({
        username: editData.username || '',
        email: editData.email || '',
        password: '', // Don't populate password in edit mode
        role_id: roleId,
        full_name: editData.full_name || '',
        birthdate: editData.birthdate ? new Date(editData.birthdate) : null
      });
    } else {
      setFormData({
        username: '',
        email: '',
        password: '',
        role_id: '',
        full_name: '',
        birthdate: null
      });
    }
    setError('');
    setShowValidation(false); // Reset validation state when form opens
    setShowFormLoading(false); // Reset form loading state when form opens
  }, [editData]);

  // Load roles on component mount
  useEffect(() => {
    const loadRoles = async () => {
      setIsLoadingRoles(true);
      try {
        const response = await dataAPI.getAllRoles({ showLoading: false });
        console.log('Roles API response:', response);
        setRoles(response.data || []);
      } catch (error) {
        console.error('Error loading roles:', error);
        setRoles(staticRoles); // Fallback to static data
      } finally {
        setIsLoadingRoles(false);
      }
    };

    if (isOpen) {
      loadRoles();
      setShowValidation(false); // Reset validation when form opens
      setShowFormLoading(false); // Reset form loading when form opens
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateBirthdate = (birthdate) => {
    if (!birthdate) return true; // Optional field
    const birthDate = new Date(birthdate);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Adjust age if birthday hasn't occurred this year
    const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) 
      ? age - 1 
      : age;
    
    return actualAge >= 18 && actualAge <= 100;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowValidation(true); // Show validation errors after submit attempt
    
    // Validation
    const requiredFields = isEditMode ? ['username', 'email', 'role_id'] : ['username', 'email', 'password', 'role_id'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      setError('Please fill in all required fields');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (formData.birthdate && !validateBirthdate(formData.birthdate)) {
      setError('Employee must be between 18 and 100 years old');
      return;
    }

    setIsSubmitting(true);
    setShowFormLoading(true);
    
    try {
      console.log(`${isEditMode ? 'Editing' : 'Creating'} employee with data:`, {
        ...formData,
        password: formData.password ? '[HIDDEN]' : 'Not provided'
      });

      // Create FormData for multipart/form-data request
      // In edit mode, use original values from editData as fallback for unchanged fields
      const formPayload = new FormData();
      
      // Helper function to get value: use formData if it has a value, otherwise use editData in edit mode
      const getValue = (field, defaultValue = '') => {
        const formValue = formData[field];
        // Check if formData has a valid value (not null, undefined, or empty string for required fields)
        if (formValue !== null && formValue !== undefined && formValue !== '') {
          return formValue;
        }
        // In edit mode, use original value from editData as fallback
        if (isEditMode && editData && editData[field] !== null && editData[field] !== undefined) {
          return editData[field];
        }
        return defaultValue;
      };
      
      // Get values - use formData first, fallback to editData in edit mode
      const username = getValue('username', '');
      const email = getValue('email', '');
      // For role_id, handle both string and number types
      // Use formData.role_id if it has a value (not null/undefined/empty), otherwise use editData in edit mode
      let roleId = '';
      if (formData.role_id !== null && formData.role_id !== undefined && formData.role_id !== '') {
        roleId = String(formData.role_id); // Ensure it's a string
      } else if (isEditMode && editData && editData.role_id !== null && editData.role_id !== undefined) {
        roleId = String(editData.role_id); // Use original value from editData as fallback
      }
      
      // For full_name, it's optional so allow empty string
      const fullName = formData.full_name !== undefined && formData.full_name !== null
        ? formData.full_name
        : (isEditMode && editData && editData.full_name !== null && editData.full_name !== undefined
          ? editData.full_name
          : '');
      
      formPayload.append('username', username);
      formPayload.append('email', email);
      formPayload.append('role_id', roleId); // Already converted to string above
      formPayload.append('full_name', fullName);
      formPayload.append('is_active', isEditMode && editData && editData.is_active !== undefined 
        ? String(editData.is_active) 
        : 'true');
      
      // Handle birthdate - use formData if provided, otherwise use editData in edit mode
      let birthdateValue = null;
      if (formData.birthdate) {
        birthdateValue = formData.birthdate;
      } else if (isEditMode && editData && editData.birthdate) {
        birthdateValue = editData.birthdate instanceof Date 
          ? editData.birthdate 
          : new Date(editData.birthdate);
      }
      
      if (birthdateValue) {
        const birthdateStr = formatDateToAPI(birthdateValue);
        if (birthdateStr) {
          formPayload.append('birthdate', birthdateStr);
        }
      }

      // Add password if provided (required for create, optional for edit)
      if (!isEditMode) {
        formPayload.append('password', formData.password);
      } else if (formData.password && formData.password.trim() !== '') {
        formPayload.append('password', formData.password);
      }

      // Note: profile_pic can be added here if you implement file upload in the form
      // formPayload.append('profile_pic', profilePicFile);

      console.log('Submitting employee payload (FormData):', {
        username: username,
        email: email,
        role_id: roleId,
        full_name: fullName,
        birthdate: birthdateValue ? formatDateToAPI(birthdateValue) : null,
        is_active: formPayload.get('is_active'),
        password: formData.password ? '[HIDDEN]' : 'Not provided',
        formData_role_id: formData.role_id,
        editData_role_id: isEditMode && editData ? editData.role_id : 'N/A'
      });
      
      const response = isEditMode 
        ? await dataAPI.updateEmployee(editData.id, formPayload, { showLoading: false })
        : await dataAPI.createEmployee(formPayload, { showLoading: false });
      
      console.log('Employee operation response:', response);
      
      // Check if the response indicates an error (business logic error)
      if (response.status === 'error') {
        setError(response.message || 'An error occurred. Please try again.');
        return; // Don't close the form, don't call onSubmit
      }
      
      console.log('Employee operation successful:', response);
      
      // Call onSubmit callback
      if (onSubmit) {
        onSubmit(response);
      }
      
      // Close form only on success
      handleClose();
      
    } catch (error) {
      console.error(`${isEditMode ? 'Edit' : 'Create'} error:`, error);
      
      // Handle different types of errors
      if (error.message) {
        setError(error.message);
      } else if (error.status === 'error' && error.message) {
        setError(error.message);
      } else {
        setError(`Failed to ${isEditMode ? 'edit' : 'create'} employee. Please try again.`);
      }
    } finally {
      setIsSubmitting(false);
      setShowFormLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role_id: '',
      full_name: '',
      birthdate: null
    });
    setError('');
    setShowPassword(false);
    setShowValidation(false); // Reset validation state
    setShowFormLoading(false); // Reset form loading state
    onClose();
  };

  // Check if all required fields are filled
  const requiredFields = isEditMode ? ['username', 'email', 'role_id'] : ['username', 'email', 'password', 'role_id'];
  const isFormComplete = requiredFields.every(field => formData[field]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          backdropFilter: 'blur(2px)'
        }}
      />
      
             {/* Modal */}
       <div 
         className="employee-form-modal"
         style={{
           position: 'fixed',
           top: '20px',
           right: '20px',
           width: '480px',
           height: 'calc(100vh - 40px)',
           backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
           boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
           zIndex: 1001,
           overflowY: 'auto',
           transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
           transition: 'transform 0.3s ease-in-out',
           borderRadius: '12px',
           scrollbarWidth: 'none', // Firefox
           msOverflowStyle: 'none' // IE and Edge
         }}
       >
         <style>
           {`
             .employee-form-modal::-webkit-scrollbar {
               display: none;
             }
           `}
         </style>
         
         {/* Form Loading Overlay */}
         {showFormLoading && (
           <div style={{
             position: 'absolute',
             top: 0,
             left: 0,
             right: 0,
             bottom: 0,
             zIndex: 1003,
             backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
             backdropFilter: 'blur(4px)',
             pointerEvents: 'none',
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             borderRadius: '12px'
           }}>
             {/* Fill Animation Loading Indicator */}
             <div className="fill-animation-container">
               {/* Background container */}
               <div className="fill-animation-background" />
               
               {/* Progress fill animation */}
               <div className="fill-animation-progress" />
               
               {/* AppIconBlue with pulse animation */}
               <img 
                 src={AppIconBlue} 
                 alt="Loading" 
                 className={`fill-animation-icon pulse-animation-icon`}
               />
             </div>
           </div>
         )}
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px 8px 20px',
          borderBottom: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: mode === 'dark' ? '#FFFFFF' : '#222222',
            margin: 0,
            fontFamily: "'Urbanist', Arial, sans-serif"
          }}>
            {isEditMode ? 'Edit Employee' : 'Add Employee'}
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '4px',
              color: mode === 'dark' ? '#FFFFFF' : '#666666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CloseIcon style={{ fontSize: '20px' }} />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} style={{ padding: '16px 20px' }}>
          {/* Error Message */}
          {error && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#ffebee',
              border: '1px solid #f44336',
              borderRadius: '6px',
              color: '#d32f2f',
              fontSize: '14px',
              fontFamily: "'Urbanist', Arial, sans-serif"
            }}>
              {error}
            </div>
          )}

          {/* Username */}
          <div style={{ marginBottom: '16px' }}>
            <TextField
              size="small"
              required
              label="Username"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              disabled={isSubmitting}
              error={showValidation && (!formData.username || formData.username.trim() === '')}
              helperText={showValidation && (!formData.username || formData.username.trim() === '') ? 'Username is required' : ''}
              style={{
                width: '100%',
                backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                color: mode === 'dark' ? '#FFFFFF' : '#222222'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? '#999' : '#666',
                  fontSize: '14px',
                },
                '& .MuiFormHelperText-root': {
                  color: '#f44336',
                  fontSize: '12px',
                },
              }}
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: '16px' }}>
            <TextField
              size="small"
              required
              type="email"
              label="Email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={isSubmitting}
              error={showValidation && (!formData.email || formData.email.trim() === '')}
              helperText={showValidation && (!formData.email || formData.email.trim() === '') ? 'Email is required' : ''}
              style={{
                width: '100%',
                backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                color: mode === 'dark' ? '#FFFFFF' : '#222222'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? '#999' : '#666',
                  fontSize: '14px',
                },
                '& .MuiFormHelperText-root': {
                  color: '#f44336',
                  fontSize: '12px',
                },
              }}
            />
          </div>

                     {/* Password - Only show in create mode */}
           {!isEditMode && (
          <div style={{ marginBottom: '16px' }}>
            <TextField
              size="small"
              required
                type={showPassword ? 'text' : 'password'}
              label="Password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
              disabled={isSubmitting}
              error={showValidation && (!formData.password || formData.password.trim() === '')}
              helperText={showValidation && (!formData.password || formData.password.trim() === '') ? 'Password is required' : ''}
              InputProps={{
                endAdornment: (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: mode === 'dark' ? '#FFFFFF' : '#666666',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center'
                }}
              >
                {showPassword ? <VisibilityOffIcon style={{ fontSize: '18px' }} /> : <VisibilityIcon style={{ fontSize: '18px' }} />}
              </button>
                )
              }}
              style={{
                width: '100%',
                backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                color: mode === 'dark' ? '#FFFFFF' : '#222222'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? '#999' : '#666',
                  fontSize: '14px',
                },
                '& .MuiFormHelperText-root': {
                  color: '#f44336',
                  fontSize: '12px',
                },
              }}
            />
          </div>
           )}

          {/* Role */}
          <div style={{ marginBottom: '16px' }}>
            <TextField
              size="small"
              required
              select
              label="Role"
              value={formData.role_id}
              onChange={(e) => handleInputChange('role_id', e.target.value)}
              disabled={isLoadingRoles || isSubmitting}
              error={showValidation && !formData.role_id}
              helperText={showValidation && !formData.role_id ? 'Role is required' : ''}
              style={{
                width: '100%',
                backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                color: mode === 'dark' ? '#FFFFFF' : '#222222'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? '#999' : '#666',
                  fontSize: '14px',
                },
                '& .MuiFormHelperText-root': {
                  color: '#f44336',
                  fontSize: '12px',
                },
                '& .MuiSelect-select': {
                  color: mode === 'dark' ? '#FFFFFF' : '#222222',
                },
              }}
            >
              <MenuItem value="">
                <em>{isLoadingRoles ? 'Loading roles...' : 'Select Role'}</em>
              </MenuItem>
              {roles.map(role => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </TextField>
          </div>

          {/* Full Name */}
          <div style={{ marginBottom: '16px' }}>
            <TextField
              size="small"
              label="Full Name"
              value={formData.full_name}
              onChange={(e) => handleInputChange('full_name', e.target.value)}
              disabled={isSubmitting}
              style={{
                width: '100%',
                backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                color: mode === 'dark' ? '#FFFFFF' : '#222222'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? '#999' : '#666',
                  fontSize: '14px',
                },
              }}
            />
          </div>

            {/* Birthdate */}
           <div style={{ marginBottom: '16px' }}>
             <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
               <DatePicker
                label="Date of Birth"
                 value={formData.birthdate}
                 onChange={(newValue) => {
                   handleInputChange('birthdate', newValue);
                 }}
                 format="dd-MM-yyyy"
                 disabled={isSubmitting}
                 maxDate={new Date()}
                 slotProps={{
                   textField: {
                     size: "small",
                     style: {
                  width: '100%',
                       backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                       color: mode === 'dark' ? '#FFFFFF' : '#222222'
                     },
                     sx: {
                       '& .MuiOutlinedInput-root': {
                         borderRadius: '8px',
                         boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                         transition: 'all 0.2s ease',
                         '& fieldset': {
                           borderColor: mode === 'dark' ? '#444' : '#ddd',
                         },
                         '&:hover fieldset': {
                           borderColor: '#26C3C0',
                           boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                         },
                         '&.Mui-focused fieldset': {
                           borderColor: '#26C3C0',
                           boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                         },
                       },
                       '& .MuiInputLabel-root': {
                         color: mode === 'dark' ? '#999' : '#666',
                  fontSize: '14px',
                       },
                     }
                  }
                }}
              />
             </LocalizationProvider>
            </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || !isFormComplete}
            style={{
              width: '100%',
              padding: '14px 24px',
              backgroundColor: isFormComplete && !isSubmitting ? '#26C3C0' : (mode === 'dark' ? '#444' : '#666'),
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: isFormComplete && !isSubmitting ? 'pointer' : 'not-allowed',
              fontFamily: "'Urbanist', Arial, sans-serif",
              transition: 'background-color 0.2s ease',
              boxShadow: isFormComplete && !isSubmitting ? '0 2px 8px rgba(38, 195, 192, 0.3)' : 'none',
              opacity: isSubmitting ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (isFormComplete && !isSubmitting) {
                e.target.style.backgroundColor = '#22B0AD';
              } else {
                e.target.style.backgroundColor = mode === 'dark' ? '#555' : '#777';
              }
            }}
            onMouseLeave={(e) => {
              if (isFormComplete && !isSubmitting) {
                e.target.style.backgroundColor = '#26C3C0';
              } else {
                e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#666';
              }
            }}
          >
            {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Employee' : 'Add Employee')}
          </button>
        </form>
      </div>
    </>
  );
};

export default EmployeeForm; 